import { useMemo } from 'react';
import { INSIGHTS } from '@/constants/testIds';

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfWeek(d) {
    // Monday start
    const day = (d.getUTCDay() + 6) % 7;
    const out = new Date(d);
    out.setUTCDate(out.getUTCDate() - day);
    out.setUTCHours(0, 0, 0, 0);
    return out;
}

function ymd(d) {
    return d.toISOString().slice(0, 10);
}

function intensity(value, max) {
    if (max <= 0 || value <= 0) return 0;
    // 1..4 buckets — keeps the swatch readable on tiny screens.
    const ratio = value / max;
    if (ratio < 0.25) return 1;
    if (ratio < 0.5) return 2;
    if (ratio < 0.75) return 3;
    return 4;
}

const LEVEL_BG = [
    'bg-foreground/[0.04] border-foreground/5',
    'bg-[#00FF9D]/15 border-[#00FF9D]/25',
    'bg-[#00FF9D]/30 border-[#00FF9D]/35',
    'bg-[#00FF9D]/55 border-[#00FF9D]/55',
    'bg-[#00FF9D] border-[#00FF9D] shadow-neon',
];

const MONTH_LABEL = (d) =>
    d.toLocaleDateString(undefined, { month: 'short' });

/**
 * GitHub-style training heatmap.
 * `days` = array of {date: 'YYYY-MM-DD', volume, set_count, pr_count}.
 * `weeks` = how many weeks back to render. Builds a grid of 7×weeks columns.
 */
export function CalendarHeatmap({ days = [], weeks = 16, units = 'kg' }) {
    const { columns, max, hasAny } = useMemo(() => {
        const map = new Map(days.map((d) => [d.date, d]));
        const todayUTC = new Date();
        todayUTC.setUTCHours(0, 0, 0, 0);
        const endWeek = startOfWeek(todayUTC);
        const startWeek = new Date(endWeek);
        startWeek.setUTCDate(startWeek.getUTCDate() - (weeks - 1) * 7);

        const cols = [];
        for (let w = 0; w < weeks; w++) {
            const colStart = new Date(startWeek);
            colStart.setUTCDate(colStart.getUTCDate() + w * 7);
            const cells = [];
            for (let d = 0; d < 7; d++) {
                const cellDate = new Date(colStart);
                cellDate.setUTCDate(cellDate.getUTCDate() + d);
                const key = ymd(cellDate);
                const future = cellDate.getTime() > todayUTC.getTime();
                cells.push({
                    date: key,
                    data: map.get(key) || null,
                    future,
                });
            }
            cols.push({ start: colStart, cells });
        }

        let m = 0;
        let any = false;
        for (const d of days) {
            if (d.volume > 0) any = true;
            if (d.set_count > m) m = d.set_count;
        }
        return { columns: cols, max: m, hasAny: any };
    }, [days, weeks]);

    return (
        <div data-testid={INSIGHTS.heatmap} className="w-full">
            {/* Month labels */}
            <div className="flex pl-7 gap-[3px] mb-1.5">
                {columns.map((col, i) => {
                    const showMonth =
                        i === 0 || col.start.getUTCDate() <= 7;
                    return (
                        <span
                            key={i}
                            className="w-3 sm:w-3.5 uppercase-mono text-muted-foreground text-[10px] leading-3 text-center"
                        >
                            {showMonth ? MONTH_LABEL(col.start) : ''}
                        </span>
                    );
                })}
            </div>

            <div className="flex gap-[3px]">
                {/* Weekday labels (Mon/Wed/Fri) */}
                <div className="flex flex-col gap-[3px] pr-1 pt-[1px]">
                    {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((c, i) => (
                        <span
                            key={i}
                            className="h-3 sm:h-3.5 w-3 uppercase-mono text-muted-foreground text-[10px] leading-3 text-center"
                        >
                            {i % 2 === 0 ? c : ''}
                        </span>
                    ))}
                </div>

                {columns.map((col, ci) => (
                    <div
                        key={ci}
                        className="flex flex-col gap-[3px]"
                    >
                        {col.cells.map((cell) => {
                            const lvl = cell.data
                                ? intensity(cell.data.set_count, max)
                                : 0;
                            const cls = cell.future
                                ? 'bg-transparent border-transparent'
                                : LEVEL_BG[lvl];
                            const title = cell.future
                                ? cell.date
                                : cell.data
                                  ? `${cell.date} · ${cell.data.set_count} sets · ${Math.round(cell.data.volume).toLocaleString()} ${units}${cell.data.pr_count ? ` · ${cell.data.pr_count} PR` : ''}`
                                  : `${cell.date} · rest`;
                            return (
                                <span
                                    key={cell.date}
                                    title={title}
                                    data-testid={`${INSIGHTS.heatmapDay}-${cell.date}`}
                                    className={`w-3 sm:w-3.5 h-3 sm:h-3.5 rounded-[3px] border ${cls} transition-colors`}
                                />
                            );
                        })}
                    </div>
                ))}
            </div>

            {/* Legend */}
            <div className="mt-3 flex items-center justify-between">
                <span className="uppercase-mono text-muted-foreground">
                    Last {weeks} weeks
                </span>
                <div className="flex items-center gap-1.5">
                    <span className="uppercase-mono text-muted-foreground">
                        less
                    </span>
                    {LEVEL_BG.map((cls, i) => (
                        <span
                            key={i}
                            className={`w-3 h-3 rounded-[3px] border ${cls}`}
                        />
                    ))}
                    <span className="uppercase-mono text-muted-foreground">
                        more
                    </span>
                </div>
            </div>

            {!hasAny && (
                <p
                    data-testid={INSIGHTS.heatmapEmpty}
                    className="mt-4 text-sm text-muted-foreground"
                >
                    No completed sets yet. Log a workout to see your streak
                    light up.
                </p>
            )}
        </div>
    );
}
